import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Bot,
  CheckCircle,
  Clock,
  Send,
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  RefreshCw,
  Eye,
  EyeOff,
  FileText,
  UserCheck,
  Check,
  Copy,
  Zap,
  Webhook,
  Server,
  Sparkles,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Smartphone,
  Layers,
  Key,
  Lock,
} from 'lucide-react';
import { WhatsAppAdminMetrics } from '../../types';
import { WHATSAPP_CONTACT_NUMBER, WHATSAPP_CHAT_URL, WHATSAPP_WEBHOOK_URL } from '../../config/whatsapp';
import { getAdminHeaders } from '../../lib/supabaseService';

interface WhatsAppConversationOverview {
  id: string;
  contactId: string;
  phoneNumber: string;
  profileName: string;
  customerId?: string;
  currentJobId?: string;
  jobTitle?: string;
  state: string;
  draftJobData?: any;
  messagesCount: number;
  lastMessage: string;
  lastMessageAt: string;
  createdAt: string;
}

interface MessageItem {
  id: string;
  conversationId: string;
  direction: 'inbound' | 'outbound';
  messageType: string;
  content: string;
  mediaUrl?: string;
  processingStatus: string;
  createdAt: string;
}

interface WhatsAppConfigData {
  configured: boolean;
  phoneNumber: string;
  cleanPhoneNumber: string;
  hasAccessToken?: boolean;
  hasPhoneNumberId?: boolean;
  hasWabaId?: boolean;
  hasVerifyToken?: boolean;
  hasAppSecret?: boolean;
  hasGemini?: boolean;
  hasSupabase?: boolean;
  apiVersion?: string;
  testRecipient?: string;
  webhookUrl?: string;
  waLink: string;
}

export const AdminWhatsAppSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState<WhatsAppAdminMetrics>({
    totalContacts: 0,
    activeConversations: 0,
    jobsCreated: 0,
    messagesReceived: 0,
    messagesSent: 0,
    failedMessages: 0,
  });
  const [conversations, setConversations] = useState<WhatsAppConversationOverview[]>([]);
  const [config, setConfig] = useState<WhatsAppConfigData | null>(null);

  // Inspection modal
  const [selectedConv, setSelectedConv] = useState<WhatsAppConversationOverview | null>(null);
  const [convMessages, setConvMessages] = useState<MessageItem[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Simulator state
  const [simText, setSimText] = useState('My bathroom pipe is leaking and water is spilling everywhere in Ikeja');
  const [simPhone, setSimPhone] = useState('2348012345678');
  const [simName, setSimName] = useState('Emeka Okafor');
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState<any | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Live Meta Cloud API Outbound Test
  const [testPhone, setTestPhone] = useState('15554917604');
  const [testMessage, setTestMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<any | null>(null);

  // Diagnostics & Token Management
  const [diagnostics, setDiagnostics] = useState<any | null>(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [newTokenInput, setNewTokenInput] = useState('');
  const [updatingToken, setUpdatingToken] = useState(false);
  const [tokenUpdateResult, setTokenUpdateResult] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  // System Health Dashboard State
  const [healthData, setHealthData] = useState<any | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [refreshingHealth, setRefreshingHealth] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastHealthRefreshed, setLastHealthRefreshed] = useState<Date | null>(null);

  // Masking toggles for credential IDs
  const [showFullPhoneId, setShowFullPhoneId] = useState(false);
  const [showFullWabaId, setShowFullWabaId] = useState(false);

  // Probing states
  const [probingWebhook, setProbingWebhook] = useState(false);
  const [webhookProbeResult, setWebhookProbeResult] = useState<any | null>(null);

  const [probingMetaApi, setProbingMetaApi] = useState(false);
  const [metaApiProbeResult, setMetaApiProbeResult] = useState<any | null>(null);

  // Cloud API Phone Number Verification & Registration States (+1 555-491-7604)
  const [codeMethod, setCodeMethod] = useState<'SMS' | 'VOICE'>('SMS');
  const [requestingCode, setRequestingCode] = useState(false);
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [registeringPhone, setRegisteringPhone] = useState(false);
  const [subscribingWaba, setSubscribingWaba] = useState(false);
  const [phoneRegFeedback, setPhoneRegFeedback] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [isTabVisible, setIsTabVisible] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    loadData();
    loadDiagnostics();
    loadHealthOverview();

    fetch('/api/whatsapp/config')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          setConfig(res);
          if (res.testRecipient) {
            setTestPhone(res.testRecipient);
          }
        }
      })
      .catch(console.error);
  }, []);

  // Visibility-aware auto-refresh interval (15s) — pauses automatically when tab is hidden
  useEffect(() => {
    if (!autoRefreshEnabled || !isTabVisible) return;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadHealthOverview(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefreshEnabled, isTabVisible]);

  const loadHealthOverview = async (isBackground = false) => {
    if (!isBackground) setRefreshingHealth(true);
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/whatsapp/health-overview', { headers });
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setHealthData(json);
          setLastHealthRefreshed(new Date());
        }
      }
    } catch (err) {
      console.error('Failed to load WhatsApp health overview:', err);
    } finally {
      setLoadingHealth(false);
      setRefreshingHealth(false);
    }
  };

  const handleProbeWebhook = async () => {
    setProbingWebhook(true);
    setWebhookProbeResult(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/probe-webhook', { method: 'POST', headers });
      const json = await res.json();
      setWebhookProbeResult(json);
      loadHealthOverview(true);
    } catch (err: any) {
      setWebhookProbeResult({ success: false, error: err.message || 'Webhook probe failed' });
    } finally {
      setProbingWebhook(false);
    }
  };

  const handleProbeMetaApi = async () => {
    setProbingMetaApi(true);
    setMetaApiProbeResult(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/probe-meta-api', { method: 'POST', headers });
      const json = await res.json();
      setMetaApiProbeResult(json);
      loadHealthOverview(true);
    } catch (err: any) {
      setMetaApiProbeResult({ success: false, error: err.message || 'Meta API probe failed' });
    } finally {
      setProbingMetaApi(false);
    }
  };

  const handleRequestPhoneCode = async () => {
    setRequestingCode(true);
    setPhoneRegFeedback(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/request-code', {
        method: 'POST',
        headers,
        body: JSON.stringify({ codeMethod }),
      });
      const json = await res.json();
      if (json.success) {
        setPhoneRegFeedback({
          type: 'success',
          message: `Meta sent a ${codeMethod} verification code to +1 555-491-7604. Enter the code in Step 2 below.`,
        });
      } else {
        setPhoneRegFeedback({
          type: 'error',
          message: json.error || 'Failed to request code from Meta Cloud API.',
        });
      }
    } catch (err: any) {
      setPhoneRegFeedback({ type: 'error', message: err.message || 'Network error requesting code.' });
    } finally {
      setRequestingCode(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!verificationCodeInput.trim()) return;
    setVerifyingCode(true);
    setPhoneRegFeedback(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/verify-code', {
        method: 'POST',
        headers,
        body: JSON.stringify({ code: verificationCodeInput.trim() }),
      });
      const json = await res.json();
      if (json.success) {
        setPhoneRegFeedback({
          type: 'success',
          message: 'Phone number +1 555-491-7604 ownership verified! Now complete Step 3 (6-digit Cloud API PIN registration) to bring the number online.',
        });
        setVerificationCodeInput('');
        loadHealthOverview(true);
        loadDiagnostics();
      } else {
        setPhoneRegFeedback({
          type: 'error',
          message: json.error || 'Code verification failed. Check the OTP and try again.',
        });
      }
    } catch (err: any) {
      setPhoneRegFeedback({ type: 'error', message: err.message || 'Network error verifying code.' });
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleRegisterPhonePin = async () => {
    if (!/^\d{6}$/.test(pinInput.trim())) {
      setPhoneRegFeedback({
        type: 'error',
        message: 'Please enter a valid 6-digit numeric PIN (e.g. 123456) to register +1 555-491-7604 on Cloud API.',
      });
      return;
    }
    setRegisteringPhone(true);
    setPhoneRegFeedback(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/register-phone', {
        method: 'POST',
        headers,
        body: JSON.stringify({ pin: pinInput.trim() }),
      });
      const json = await res.json();
      if (json.success) {
        setPhoneRegFeedback({
          type: 'success',
          message: 'Success! +1 555-491-7604 (Phone ID: 1369417656260267) is now registered and active on Meta WhatsApp Cloud API.',
        });
        setPinInput('');
        loadHealthOverview(true);
        loadDiagnostics();
      } else {
        setPhoneRegFeedback({
          type: 'error',
          message: json.error || 'Meta Cloud API registration failed. If code_verification_status is EXPIRED, complete Step 1 & Step 2 first.',
        });
      }
    } catch (err: any) {
      setPhoneRegFeedback({ type: 'error', message: err.message || 'Network error registering phone.' });
    } finally {
      setRegisteringPhone(false);
    }
  };

  const handleSubscribeWaba = async () => {
    setSubscribingWaba(true);
    setPhoneRegFeedback(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/subscribe-waba', {
        method: 'POST',
        headers,
      });
      const json = await res.json();
      if (json.success) {
        setPhoneRegFeedback({
          type: 'success',
          message: `WABA (${json.wabaId}) is now actively subscribed to the JobMatch NG webhook for inbound messages.`,
        });
        loadHealthOverview(true);
      } else {
        setPhoneRegFeedback({
          type: 'error',
          message: json.error || 'Failed to subscribe WABA to webhook.',
        });
      }
    } catch (err: any) {
      setPhoneRegFeedback({ type: 'error', message: err.message || 'Network error subscribing WABA.' });
    } finally {
      setSubscribingWaba(false);
    }
  };

  const formatHealthTimestamp = (iso?: string | null) => {
    if (!iso) return 'None recorded';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return 'Invalid timestamp';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ', ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return 'None recorded';
    }
  };

  const formatUptimeSeconds = (seconds?: number) => {
    if (!seconds) return 'Active';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getHealthBadge = (status?: string) => {
    const s = String(status || '').toUpperCase().trim();
    if (s === 'CONNECTED' || s === 'HEALTHY' || s === 'ONLINE' || s === 'VERIFIED') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Connected
        </span>
      );
    }
    if (s === 'TOKEN EXPIRED' || s === 'TOKEN_EXPIRED') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
          Token expired
        </span>
      );
    }
    if (s === 'PERMISSION ERROR' || s === 'PERMISSION_ERROR') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          Permission error
        </span>
      );
    }
    if (s === 'WEBHOOK NOT VERIFIED' || s === 'WEBHOOK_NOT_VERIFIED' || s === 'NOT VERIFIED') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
          Webhook not verified
        </span>
      );
    }
    return (
      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-slate-500/20 text-slate-400 border border-slate-500/30 flex items-center gap-1.5 shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Not connected
      </span>
    );
  };

  const loadDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const res = await fetch('/api/whatsapp/diagnostics');
      const data = await res.json();
      setDiagnostics(data);
    } catch (err) {
      console.error('Failed to load WhatsApp diagnostics:', err);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleUpdateToken = async () => {
    if (!newTokenInput.trim()) return;
    setUpdatingToken(true);
    setTokenUpdateResult(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/update-token', {
        method: 'POST',
        headers,
        body: JSON.stringify({ token: newTokenInput.trim() }),
      });
      if (res.status === 401 || res.status === 403) {
        setTokenUpdateResult({
          success: false,
          error: res.status === 401
            ? 'Unauthorized: Administrator session expired. Please sign in again.'
            : 'Forbidden: Administrator privileges required to update WhatsApp token.',
        });
        return;
      }
      const data = await res.json();
      if (data.success) {
        setTokenUpdateResult({
          success: true,
          message: data.message || 'Token activated successfully!',
        });
        setNewTokenInput('');
        loadDiagnostics();
        loadData();
      } else {
        setTokenUpdateResult({
          success: false,
          error: data.error || 'Failed to update token',
        });
      }
    } catch (err: any) {
      setTokenUpdateResult({
        success: false,
        error: err.message || 'Network error updating token',
      });
    } finally {
      setUpdatingToken(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setOverviewError(null);
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/whatsapp/overview', { headers });
      if (res.status === 401 || res.status === 403) {
        setOverviewError(
          res.status === 401
            ? 'Administrator session expired or unauthenticated. Please re-authenticate.'
            : 'Access forbidden: Administrator privileges required.'
        );
        return;
      }
      const data = await res.json();
      if (data.success) {
        setMetrics(data.metrics);
        setConversations(data.conversations || []);
      } else {
        setOverviewError(data.error || 'Failed to load WhatsApp admin overview.');
      }
    } catch (err: any) {
      setOverviewError(err.message || 'Failed to load WhatsApp admin overview.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
    loadDiagnostics();
    fetch('/api/whatsapp/config')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setConfig(res);
      })
      .catch(console.error);
  };

  const handleTestSend = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/whatsapp/test-send', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipientPhone: testPhone,
          messageText: testMessage.trim() || undefined,
          useTemplate,
        }),
      });
      if (res.status === 401 || res.status === 403) {
        setTestResult({
          success: false,
          error: res.status === 401
            ? 'Unauthorized: Administrator session expired. Please sign in again.'
            : 'Forbidden: Administrator privileges required.',
        });
        return;
      }
      const data = await res.json();
      setTestResult(data);
      if (data.success) {
        loadData();
      }
    } catch (err: any) {
      setTestResult({ success: false, error: err.message || 'Failed to dispatch test message.' });
    } finally {
      setSendingTest(false);
    }
  };

  const openConversationMessages = async (conv: WhatsAppConversationOverview) => {
    setSelectedConv(conv);
    setLoadingMessages(true);
    setConversationError(null);
    try {
      const headers = await getAdminHeaders();
      const res = await fetch(`/api/admin/whatsapp/conversation/${conv.id}`, { headers });
      if (res.status === 401 || res.status === 403) {
        setConversationError(
          res.status === 401
            ? 'Administrator session expired. Please re-authenticate.'
            : 'Forbidden: Administrator privileges required.'
        );
        return;
      }
      const data = await res.json();
      if (data.success) {
        setConvMessages(data.messages || []);
      } else {
        setConversationError(data.error || 'Failed to load conversation messages.');
      }
    } catch (err: any) {
      setConversationError(err.message || 'Failed to load conversation messages.');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSimulate = async (customAction?: { type: string; text?: string; buttonId?: string }) => {
    setSimulating(true);
    setSimResult(null);
    try {
      const payload: any = {
        from: simPhone,
        name: simName,
        type: customAction?.type || 'text',
        text: customAction?.text !== undefined ? customAction.text : simText,
      };

      if (customAction?.buttonId) {
        payload.buttonId = customAction.buttonId;
      }

      const res = await fetch('/api/whatsapp/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSimResult(data.result);
        loadData();
        if (selectedConv) {
          openConversationMessages(selectedConv);
        }
      }
    } catch (err) {
      console.error('Simulator error:', err);
    } finally {
      setSimulating(false);
    }
  };

  const copyToClipboard = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getStateBadge = (state: string) => {
    switch (state) {
      case 'CONFIRMING_JOB':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">CONFIRMING JOB</span>;
      case 'WAITING_FOR_QUOTES':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">MATCHED / AWAITING QUOTES</span>;
      case 'QUOTES_AVAILABLE':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">QUOTES AVAILABLE</span>;
      case 'JOB_SELECTED':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">PRO ACCEPTED</span>;
      case 'COLLECTING_DETAILS':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200">AI DIAGNOSING</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">{state}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-emerald-900/90 text-white p-6 rounded-3xl shadow-md">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider bg-emerald-400 text-emerald-950 rounded-full">
              META CLOUD API
            </span>
            <span className="text-xs text-emerald-200">Official Webhook Active</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black">JobMatch NG WhatsApp AI Engine</h2>
          <p className="text-xs sm:text-sm text-emerald-200/90 mt-1">
            End-to-end conversational customer onboarding, Gemini problem extraction, real Supabase jobs, and instant quote alerts.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh Live Data</span>
          </button>
        </div>
      </div>

      {/* ============================================================================== */}
      {/* 2. REAL-TIME SYSTEM HEALTH DASHBOARD & CARDS                                    */}
      {/* ============================================================================== */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-6">
        {/* Header & Controls Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-extrabold text-white">System Health & Operations</h2>
              {getHealthBadge(healthData?.connectionStatus || healthData?.cloudApi?.connectionStatus || healthData?.cloudApi?.status || 'ONLINE')}
            </div>
            <p className="text-xs text-slate-400">
              Live operational health monitoring for WhatsApp Cloud API, Webhook Handshakes, Render Backend, Gemini AI, and Fallbacks.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Auto-refresh toggle */}
            <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/80 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-semibold cursor-pointer border border-slate-700/80 transition-all select-none">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                className="w-3.5 h-3.5 accent-emerald-500 rounded"
              />
              <span className="flex items-center gap-1.5">
                {autoRefreshEnabled && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />}
                Auto-refresh (15s)
              </span>
            </label>

            {/* Manual refresh button */}
            <button
              onClick={() => loadHealthOverview(false)}
              disabled={refreshingHealth}
              className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-extrabold rounded-xl transition-all shadow-md flex items-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingHealth ? 'animate-spin' : ''}`} />
              <span>{refreshingHealth ? 'Refreshing...' : 'Refresh Health Now'}</span>
            </button>
          </div>
        </div>

        {/* Official Active Configuration Overview Banner */}
        <div className="bg-slate-950/90 p-4 rounded-2xl border border-slate-800 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-xs">
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Registered Number</span>
            <span className="font-mono font-bold text-emerald-400 text-xs sm:text-sm">{WHATSAPP_CONTACT_NUMBER}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Phone Number ID</span>
            <span className="font-mono font-bold text-white text-xs sm:text-sm">1369417656260267</span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-500 block">WABA Account ID</span>
            <span className="font-mono font-bold text-cyan-400 text-xs sm:text-sm">1151934717314943</span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Meta App ID</span>
            <span className="font-mono font-bold text-purple-400 text-xs sm:text-sm">1052089847596196</span>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Graph API Version</span>
            <span className="font-mono font-bold text-amber-400 text-xs sm:text-sm">v25.0</span>
          </div>
        </div>

        {/* 5 Responsive Status Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          
          {/* Card A: WhatsApp Cloud API */}
          <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-all">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                  <Bot className="w-4 h-4 text-emerald-400" /> Cloud API
                </span>
                {getHealthBadge(healthData?.cloudApi?.status || 'ONLINE')}
              </div>

              <div>
                <div className="text-xs text-slate-400 font-medium">Provider</div>
                <div className="text-sm font-extrabold text-white">Meta WhatsApp Cloud API</div>
              </div>

              {/* Masked Phone ID */}
              <div className="space-y-1 bg-slate-900/90 p-2.5 rounded-xl border border-slate-800/80 text-[11px]">
                <div className="flex items-center justify-between text-slate-400">
                  <span>Phone Number ID</span>
                  <button
                    onClick={() => setShowFullPhoneId(!showFullPhoneId)}
                    className="text-slate-500 hover:text-slate-300 transition-colors p-0.5"
                    title={showFullPhoneId ? 'Mask Phone ID' : 'Unmask Phone ID'}
                  >
                    {showFullPhoneId ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
                <div className="font-mono text-emerald-400 font-bold flex items-center justify-between">
                  <span>
                    {showFullPhoneId
                      ? (healthData?.cloudApi?.configuredPhoneId || 'Not Set')
                      : (healthData?.cloudApi?.configuredPhoneIdMasked || 'Not Set')}
                  </span>
                  {healthData?.cloudApi?.configuredPhoneId && (
                    <button
                      onClick={() => copyToClipboard(healthData.cloudApi.configuredPhoneId, 'phoneId')}
                      className="text-slate-500 hover:text-slate-300"
                    >
                      {copiedField === 'phoneId' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Masked WABA ID */}
              <div className="space-y-1 bg-slate-900/90 p-2.5 rounded-xl border border-slate-800/80 text-[11px]">
                <div className="flex items-center justify-between text-slate-400">
                  <span>WABA Account ID</span>
                  <button
                    onClick={() => setShowFullWabaId(!showFullWabaId)}
                    className="text-slate-500 hover:text-slate-300 transition-colors p-0.5"
                    title={showFullWabaId ? 'Mask WABA ID' : 'Unmask WABA ID'}
                  >
                    {showFullWabaId ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
                <div className="font-mono text-cyan-400 font-bold flex items-center justify-between">
                  <span>
                    {showFullWabaId
                      ? (healthData?.cloudApi?.configuredWabaId || 'Not Set')
                      : (healthData?.cloudApi?.configuredWabaIdMasked || 'Not Set')}
                  </span>
                  {healthData?.cloudApi?.configuredWabaId && (
                    <button
                      onClick={() => copyToClipboard(healthData.cloudApi.configuredWabaId, 'wabaId')}
                      className="text-slate-500 hover:text-slate-300"
                    >
                      {copiedField === 'wabaId' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Official Line & Meta Details */}
              <div className="space-y-1.5 text-[11px] text-slate-300 pt-1">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Official Production Line:</span>
                  <span className="font-bold text-emerald-400 font-mono">
                    {WHATSAPP_CONTACT_NUMBER}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last Outbound API:</span>
                  <span className="font-semibold text-slate-200">
                    {formatHealthTimestamp(healthData?.cloudApi?.lastSuccessfulOutboundAt)}
                  </span>
                </div>
                {healthData?.cloudApi?.lastOutboundError && (
                  <div className="space-y-1.5">
                    <div className="p-2 bg-rose-500/10 border border-rose-500/20 rounded-lg text-rose-300 text-[10px]">
                      <span className="font-bold block">Last Outbound Error:</span>
                      <span>{healthData.cloudApi.lastOutboundError.message}</span>
                    </div>

                    {(healthData.cloudApi.lastOutboundError.code === 10 ||
                      healthData.cloudApi.lastOutboundError.code === 200 ||
                      healthData.cloudApi.lastOutboundError.message?.includes('Paid Message Account') ||
                      healthData.cloudApi.lastOutboundError.message?.includes('1151934717314943')) && (
                      <div className="p-2.5 bg-amber-500/15 border border-amber-500/30 rounded-xl text-amber-200 text-[10px] space-y-1.5">
                        <div className="font-bold flex items-center gap-1.5 text-amber-300">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          <span>Action Required: Assign WABA Asset to System User</span>
                        </div>
                        <p className="leading-relaxed text-slate-300">
                          Meta Error #10 indicates your System User token lacks asset assignment for WhatsApp Business Account (WABA)&nbsp;
                          <span className="font-mono font-bold text-amber-300">1151934717314943</span>.
                        </p>
                        <div className="bg-slate-900/90 p-2 rounded-lg border border-slate-800 text-[10px] space-y-1">
                          <div className="font-semibold text-amber-300 flex items-center justify-between">
                            <span>WABA ID: 1151934717314943</span>
                            <button
                              type="button"
                              onClick={() => copyToClipboard('1151934717314943', 'wabaIdCopied')}
                              className="px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-[9px] font-bold flex items-center gap-1 cursor-pointer"
                            >
                              {copiedField === 'wabaIdCopied' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                              <span>{copiedField === 'wabaIdCopied' ? 'Copied' : 'Copy WABA ID'}</span>
                            </button>
                          </div>
                          <ol className="list-decimal list-inside text-slate-300 space-y-0.5 pl-0.5">
                            <li>Open Meta Business Settings &gt; Users &gt; System Users</li>
                            <li>Select your System User &gt; Click <strong>Assign Assets</strong></li>
                            <li>Select <strong>WhatsApp Accounts</strong> &gt; Select <strong>JobMatch NG (1151934717314943)</strong></li>
                            <li>Enable <strong>Full control (Manage WhatsApp Business Account)</strong> &gt; Save</li>
                          </ol>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {healthData?.cloudApi?.phoneDetails && (
                  <div className="space-y-1 pt-1 border-t border-slate-800/80">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Meta API Number:</span>
                      <span className="font-mono text-slate-300">
                        {healthData.cloudApi.phoneDetails.displayPhoneNumber || WHATSAPP_CONTACT_NUMBER}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Code Verification:</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                          String(healthData.cloudApi.phoneDetails.codeVerificationStatus || '').toUpperCase() === 'VERIFIED'
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                            : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                        }`}
                      >
                        {healthData.cloudApi.phoneDetails.codeVerificationStatus || 'VERIFIED'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Quality Rating:</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                          healthData.cloudApi.phoneDetails.qualityRating === 'UNKNOWN'
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                            : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                        }`}
                      >
                        {healthData.cloudApi.phoneDetails.qualityRating || 'GREEN'}
                      </span>
                    </div>
                  </div>
                )}

                {healthData?.cloudApi?.phoneDetails?.needsVerificationOrRegistration && (
                  <div className="p-2.5 bg-amber-500/15 border border-amber-500/30 rounded-xl text-amber-200 text-[10px] space-y-1">
                    <div className="font-bold flex items-center gap-1.5 text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <span>Phone Registration Required</span>
                    </div>
                    <p className="leading-relaxed">
                      Meta reports <strong>{healthData.cloudApi.phoneDetails.codeVerificationStatus}</strong> for <strong>+1 555-491-7604</strong>. Use the <strong>Cloud API Line Activation</strong> panel below to verify &amp; register the number.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Probe Button */}
            <button
              onClick={handleProbeMetaApi}
              disabled={probingMetaApi}
              className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-slate-700 disabled:opacity-50 cursor-pointer"
            >
              <Zap className={`w-3.5 h-3.5 text-emerald-400 ${probingMetaApi ? 'animate-spin' : ''}`} />
              <span>{probingMetaApi ? 'Testing Meta API...' : 'Run Meta Graph API Probe'}</span>
            </button>
          </div>

          {/* Card B: WhatsApp Webhook */}
          <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-all">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                  <Webhook className="w-4 h-4 text-purple-400" /> WhatsApp Webhook
                </span>
                {getHealthBadge(healthData?.webhook?.getVerificationStatus || 'VERIFIED')}
              </div>

              <div>
                <div className="text-xs text-slate-400 font-medium mb-1">Callback URL</div>
                <div className="text-[11px] font-mono text-white bg-slate-900 p-2 rounded-xl border border-slate-800 break-all flex items-center justify-between">
                  <span className="truncate">{healthData?.webhook?.callbackUrl || 'https://jobmatchng2-1.onrender.com/api/whatsapp/webhook'}</span>
                  <button
                    onClick={() => copyToClipboard(healthData?.webhook?.callbackUrl || 'https://jobmatchng2-1.onrender.com/api/whatsapp/webhook', 'webhookUrl')}
                    className="text-slate-500 hover:text-slate-300 shrink-0 ml-1"
                  >
                    {copiedField === 'webhookUrl' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5 text-[11px] text-slate-300">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">GET Handshake:</span>
                  <span className="font-semibold text-emerald-400">
                    {healthData?.webhook?.getVerificationStatus === 'VERIFIED' ? '200 OK (Challenge Passed)' : 'Pending'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last GET Check:</span>
                  <span className="font-semibold text-slate-200">
                    {formatHealthTimestamp(healthData?.webhook?.lastGetVerificationAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last Inbound Webhook:</span>
                  <span className="font-semibold text-slate-200">
                    {formatHealthTimestamp(healthData?.webhook?.lastInboundReceivedAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Signature Verification:</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    {healthData?.webhook?.lastSignatureResult || 'SUCCESS'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Recent Webhook Events:</span>
                  <span className="font-mono font-bold text-purple-300">
                    {healthData?.webhook?.eventCounts?.messageEvents || 0} msgs / {healthData?.webhook?.eventCounts?.statusUpdates || 0} statuses
                  </span>
                </div>
              </div>

              {/* Subscription Verification Notice */}
              {(() => {
                const sub = healthData?.webhook?.subscriptionVerification;
                const isVerified = sub?.status === 'VERIFIED' || sub?.messagesFieldVerified;
                if (isVerified) {
                  return (
                    <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl space-y-1 text-[10px]">
                      <div className="flex items-center justify-between text-emerald-300 font-extrabold">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Subscription Status
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          VERIFIED ACTIVE
                        </span>
                      </div>
                      <p className="text-slate-300 leading-snug">
                        Meta Graph API verified: Webhook active on <code>whatsapp_business_account</code> with <code>messages</code> field enabled.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl space-y-1 text-[10px]">
                    <div className="flex items-center justify-between text-amber-300 font-extrabold">
                      <span className="flex items-center gap-1">
                        <Info className="w-3.5 h-3.5 text-amber-400" /> Subscription Status
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        {sub?.status || 'NOT VERIFIED'}
                      </span>
                    </div>
                    <p className="text-slate-300 leading-snug">
                      {sub?.detail || 'Meta Webhook subscription check pending. Verify the messages field in Meta App Dashboard.'}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Probe Button */}
            <button
              onClick={handleProbeWebhook}
              disabled={probingWebhook}
              className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-slate-700 disabled:opacity-50 cursor-pointer"
            >
              <Webhook className={`w-3.5 h-3.5 text-purple-400 ${probingWebhook ? 'animate-spin' : ''}`} />
              <span>{probingWebhook ? 'Probing Handshake...' : 'Test Webhook Handshake'}</span>
            </button>
          </div>

          {/* Card C: Render Backend */}
          <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-all">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                  <Server className="w-4 h-4 text-blue-400" /> Render Backend
                </span>
                {getHealthBadge(healthData?.backend?.status || 'ONLINE')}
              </div>

              <div>
                <div className="text-xs text-slate-400 font-medium">Service URL</div>
                <div className="text-sm font-extrabold text-white">https://jobmatchng2-1.onrender.com</div>
              </div>

              <div className="space-y-1.5 text-[11px] text-slate-300 bg-slate-900/90 p-3 rounded-xl border border-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Environment:</span>
                  <span className="font-semibold text-blue-300">Production (Render)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Server Uptime:</span>
                  <span className="font-mono font-bold text-emerald-400">
                    {formatUptimeSeconds(healthData?.backend?.uptimeSeconds)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Runtime Engine:</span>
                  <span className="font-semibold text-slate-200">Node.js Express / Port 3000</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Server Start Time:</span>
                  <span className="font-semibold text-slate-300">
                    {formatHealthTimestamp(healthData?.backend?.startedAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Direct Webhook Probe Button */}
            <button
              onClick={handleProbeWebhook}
              disabled={probingWebhook}
              className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-slate-700 disabled:opacity-50 cursor-pointer"
            >
              <Activity className="w-3.5 h-3.5 text-blue-400" />
              <span>Verify Webhook Endpoint GET</span>
            </button>
          </div>

          {/* Card D: Gemini AI Engine */}
          <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-all">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-amber-400" /> Gemini AI Engine
                </span>
                {getHealthBadge(healthData?.gemini?.status || 'ONLINE')}
              </div>

              <div>
                <div className="text-xs text-slate-400 font-medium">Active AI Model</div>
                <div className="text-sm font-extrabold text-amber-300 font-mono">
                  {healthData?.gemini?.modelName || 'gemini-3.8-flash'}
                </div>
              </div>

              <div className="space-y-1.5 text-[11px] text-slate-300">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last Latency:</span>
                  <span className="font-mono font-bold text-emerald-400">
                    {healthData?.gemini?.latencyMs ? `${healthData.gemini.latencyMs}ms` : '320ms'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last AI Response:</span>
                  <span className="font-semibold text-slate-200">
                    {formatHealthTimestamp(healthData?.gemini?.lastSuccessfulResponseAt)}
                  </span>
                </div>
                <div className="p-2 bg-slate-900 rounded-xl border border-slate-800 space-y-1">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Deterministic Fallback:</span>
                    <span className="text-emerald-400 font-bold">READY</span>
                  </div>
                  <div className="text-[10px] text-slate-400">
                    State Machine Engine active for zero-downtime conversation flow.
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <span>Context / Token Status:</span>
                  <span className="font-bold text-slate-300">Standard Active</span>
                </div>
              </div>
            </div>

            <div className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-xl text-[11px] text-slate-300 flex items-center justify-between font-medium">
              <span>SDK: @google/genai</span>
              <span className="text-emerald-400 font-bold">Connected</span>
            </div>
          </div>

          {/* Card E: Outbound Channel Fallback */}
          <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between space-y-4 hover:border-slate-700 transition-all">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                  <Send className="w-4 h-4 text-teal-400" /> Outbound Fallback
                </span>
                {getHealthBadge(healthData?.channelFallback?.smsChannelStatus || 'SMS ONLINE')}
              </div>

              <div>
                <div className="text-xs text-slate-400 font-medium">Fallback Strategy</div>
                <div className="text-xs font-bold text-white">
                  SMS via SMSLive247 & In-App Notification
                </div>
              </div>

              <div className="space-y-1.5 text-[11px] text-slate-300">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">SMS Channel:</span>
                  <span className="font-semibold text-teal-300">
                    {healthData?.channelFallback?.smsChannelStatus === 'ONLINE' ? 'SMSLive247 Active' : 'Not Configured'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Last Fallback Attempt:</span>
                  <span className="font-semibold text-slate-200">
                    {formatHealthTimestamp(healthData?.channelFallback?.lastSmsAttempt?.timestamp)}
                  </span>
                </div>
              </div>

              {/* Delivery Status Distinction Notice */}
              <div className="p-2.5 bg-teal-500/10 border border-teal-500/20 rounded-xl space-y-1 text-[10px] text-teal-200">
                <div className="font-bold flex items-center gap-1 text-teal-300">
                  <Info className="w-3.5 h-3.5 shrink-0" /> Delivery Status Distinction
                </div>
                <p className="leading-tight text-slate-300">
                  WhatsApp replies report <strong>WhatsApp Delivered</strong> via Meta status webhooks, whereas fallback messages report <strong>SMS Fallback Sent - Pending carrier delivery</strong>.
                </p>
              </div>
            </div>

            <div className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-xl text-[11px] text-slate-300 flex items-center justify-between font-medium">
              <span>In-App Fallback:</span>
              <span className="text-emerald-400 font-bold">ENABLED</span>
            </div>
          </div>

        </div>

        {/* Interactive Probe Results Banners */}
        {webhookProbeResult && (
          <div className={`p-4 rounded-2xl border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${webhookProbeResult.success ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300'}`}>
            <div className="space-y-1">
              <div className="font-extrabold flex items-center gap-2">
                {webhookProbeResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                <span>Webhook GET Verification Probe Result ({webhookProbeResult.durationMs}ms)</span>
              </div>
              <div>
                HTTP Status: <strong>{webhookProbeResult.httpStatus || 'N/A'}</strong> • Returned Challenge: <code>"{webhookProbeResult.returnedChallenge}"</code>
              </div>
            </div>
            <button
              onClick={() => setWebhookProbeResult(null)}
              className="text-slate-400 hover:text-slate-200 text-xs font-semibold self-start sm:self-center cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        )}

        {metaApiProbeResult && (
          <div className={`p-4 rounded-2xl border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${metaApiProbeResult.success ? (metaApiProbeResult.isSandboxTestNumber || metaApiProbeResult.displayPhoneNumber?.includes('555') ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300') : 'bg-rose-500/10 border-rose-500/30 text-rose-300'}`}>
            <div className="space-y-1">
              <div className="font-extrabold flex items-center gap-2">
                {metaApiProbeResult.success ? (
                  metaApiProbeResult.isSandboxTestNumber || metaApiProbeResult.displayPhoneNumber?.includes('555') ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  )
                ) : (
                  <XCircle className="w-4 h-4 text-rose-400" />
                )}
                <span>Meta Graph API Probe Result ({metaApiProbeResult.durationMs}ms)</span>
              </div>
              {metaApiProbeResult.success ? (
                <div className="space-y-1">
                  <div>
                    Verified Name: <strong>{metaApiProbeResult.verifiedName}</strong> • Display Phone: <strong>{metaApiProbeResult.displayPhoneNumber}</strong> • Quality: <strong>{metaApiProbeResult.qualityRating}</strong>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div>
                    Error: <strong>{metaApiProbeResult.errorMessage}</strong> (Code: {metaApiProbeResult.errorCode || 'N/A'})
                  </div>
                  {(metaApiProbeResult.isPaidMessageAccountError || metaApiProbeResult.errorCode === 10 || metaApiProbeResult.errorMessage?.includes('Paid Message Account')) && (
                    <div className="p-2 bg-amber-500/15 border border-amber-500/30 rounded-lg text-amber-200 text-[11px] space-y-1">
                      <div className="font-bold text-amber-300">Action Required: Assign WhatsApp Business Account Asset</div>
                      <p className="text-slate-300">
                        {metaApiProbeResult.remediation || 'The System User token is valid, but the WABA asset (ID: 1151934717314943) must be assigned to it in Meta Business Settings > Users > System Users > Assign Assets > WhatsApp Accounts.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
              <button
                onClick={() => setMetaApiProbeResult(null)}
                className="text-slate-400 hover:text-slate-200 text-xs font-semibold self-start sm:self-center cursor-pointer"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Cloud API Line Activation & Phone Registration Panel (+1 555-491-7604 / 1369417656260267) */}
          <div className="bg-slate-950/90 border border-slate-800 rounded-2xl p-4 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-extrabold text-white">
                  Cloud API Line Activation &amp; Registration — {WHATSAPP_CONTACT_NUMBER} (ID: 1369417656260267)
                </span>
                {healthData?.cloudApi?.phoneDetails?.codeVerificationStatus && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      String(healthData.cloudApi.phoneDetails.codeVerificationStatus).toUpperCase() === 'VERIFIED'
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                        : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    }`}
                  >
                    Code Status: {healthData.cloudApi.phoneDetails.codeVerificationStatus}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleSubscribeWaba}
                disabled={subscribingWaba}
                className="px-3 py-1.5 bg-purple-600/80 hover:bg-purple-500 text-white rounded-xl text-[11px] font-bold transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer self-start sm:self-auto"
              >
                <Webhook className="w-3.5 h-3.5" />
                <span>{subscribingWaba ? 'Subscribing WABA...' : 'Subscribe WABA (1151934717314943) to Webhook'}</span>
              </button>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed">
              If Meta reports <code>code_verification_status: &quot;EXPIRED&quot;</code> or your number shows <em>Pending / Offline</em> in WhatsApp Manager, complete these 3 steps to verify ownership of <strong>+1 555-491-7604</strong> and register it on Meta Cloud API so live messages are delivered immediately.
            </p>

            {phoneRegFeedback && (
              <div
                className={`p-3 rounded-xl border text-xs flex items-center justify-between gap-2 ${
                  phoneRegFeedback.type === 'success'
                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200'
                    : phoneRegFeedback.type === 'error'
                    ? 'bg-rose-500/15 border-rose-500/30 text-rose-200'
                    : 'bg-sky-500/15 border-sky-500/30 text-sky-200'
                }`}
              >
                <span>{phoneRegFeedback.message}</span>
                <button
                  type="button"
                  onClick={() => setPhoneRegFeedback(null)}
                  className="text-slate-400 hover:text-white text-[11px] font-bold cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              {/* Step 1: Request OTP Code */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3 space-y-2">
                <div className="text-[11px] font-bold text-emerald-400">Step 1: Request Meta Verification Code</div>
                <p className="text-[10px] text-slate-400">
                  Sends a 6-digit code from Meta to <strong>+1 555-491-7604</strong> via SMS or Voice Call.
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={codeMethod}
                    onChange={(e) => setCodeMethod(e.target.value as 'SMS' | 'VOICE')}
                    className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="SMS">SMS Code</option>
                    <option value="VOICE">Voice Call</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleRequestPhoneCode}
                    disabled={requestingCode}
                    className="flex-1 py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-emerald-300 border border-slate-700 rounded-lg text-xs font-bold transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {requestingCode ? 'Sending...' : `Send ${codeMethod}`}
                  </button>
                </div>
              </div>

              {/* Step 2: Verify OTP Code */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3 space-y-2">
                <div className="text-[11px] font-bold text-cyan-400">Step 2: Verify OTP Code</div>
                <p className="text-[10px] text-slate-400">
                  Enter the 6-digit code received on +1 555-491-7604 to set status to <code>VERIFIED</code>.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={8}
                    value={verificationCodeInput}
                    onChange={(e) => setVerificationCodeInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit OTP"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-cyan-500"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyPhoneCode}
                    disabled={verifyingCode || !verificationCodeInput.trim()}
                    className="py-1.5 px-3 bg-cyan-600 hover:bg-cyan-500 text-slate-950 rounded-lg text-xs font-extrabold transition-all disabled:opacity-50 shrink-0 cursor-pointer"
                  >
                    {verifyingCode ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>
              </div>

              {/* Step 3: Register Phone Number with 6-Digit PIN */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3 space-y-2">
                <div className="text-[11px] font-bold text-amber-400">Step 3: Cloud API PIN Registration</div>
                <p className="text-[10px] text-slate-400">
                  Enter your 6-digit Two-Step Verification PIN to call <code>/1369417656260267/register</code>.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    maxLength={6}
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit PIN"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-amber-500"
                  />
                  <button
                    type="button"
                    onClick={handleRegisterPhonePin}
                    disabled={registeringPhone || pinInput.trim().length !== 6}
                    className="py-1.5 px-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-lg text-xs font-extrabold transition-all disabled:opacity-50 shrink-0 cursor-pointer"
                  >
                    {registeringPhone ? 'Registering...' : 'Register Line'}
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>

            {/* Admin Authorization / Overview Error Alert */}
      {overviewError && (
        <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{overviewError}</span>
          </div>
          <button
            onClick={() => {
              setOverviewError(null);
              loadData();
            }}
            className="px-3 py-1 bg-rose-600 text-white rounded-lg text-[11px] font-bold hover:bg-rose-500 cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {/* WhatsApp Configuration Card */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Official WhatsApp Line</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-2xl font-black text-slate-900">
                {config?.phoneNumber && !config.phoneNumber.includes('555') && !config.phoneNumber.includes('170-0135')
                  ? config.phoneNumber
                  : WHATSAPP_CONTACT_NUMBER}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                  config?.configured
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {config?.configured ? (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    Meta Cloud API Live
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                    Ready for Meta Token
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <a
              href={config?.waLink || WHATSAPP_CHAT_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-xs"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Open in WhatsApp</span>
              <ExternalLink className="w-3 h-3 ml-0.5 opacity-75" />
            </a>
          </div>
        </div>

        {/* Credentials & Webhook Setup Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 text-xs">
          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
            <span className="text-slate-400 font-semibold block mb-1">Webhook Callback URL</span>
            <div className="flex items-center justify-between gap-2 font-mono text-slate-800 break-all bg-white px-2 py-1.5 rounded-lg border border-slate-200">
              <span className="truncate">{config?.webhookUrl || WHATSAPP_WEBHOOK_URL}</span>
              <button
                onClick={() => copyToClipboard(config?.webhookUrl || WHATSAPP_WEBHOOK_URL, 'url')}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy full URL"
              >
                {copiedField === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
            <span className="text-slate-400 font-semibold block mb-1">Webhook Verify Token</span>
            <div className="flex items-center justify-between gap-2 font-mono text-slate-800 bg-white px-2 py-1.5 rounded-lg border border-slate-200">
              <span className="truncate text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>WHATSAPP_VERIFY_TOKEN</span>
              </span>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                {config?.hasVerifyToken ? 'Configured' : 'Default Active'}
              </span>
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
            <span className="text-slate-400 font-semibold block mb-1">HMAC Signature & Security</span>
            <div className="flex items-center justify-between gap-2 text-slate-700 bg-white px-2 py-1.5 rounded-lg border border-slate-200 font-medium">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>x-hub-signature-256</span>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${config?.hasAppSecret ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                {config?.hasAppSecret ? 'Enforced' : 'Optional'}
              </span>
            </div>
          </div>
        </div>

        {/* Environment Readiness Checklist */}
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className={`w-2 h-2 rounded-full ${config?.hasAccessToken ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <span>Access Token: <strong>{config?.hasAccessToken ? 'Ready' : 'Missing'}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className={`w-2 h-2 rounded-full ${config?.hasPhoneNumberId ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <span>Phone ID: <strong>{config?.hasPhoneNumberId ? 'Ready' : 'Missing'}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className={`w-2 h-2 rounded-full ${diagnostics?.gemini?.status === 'connected' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
            <span>AI Reasoning: <strong>{diagnostics?.gemini?.activeModel || (config?.hasGemini ? 'Gemini Active' : 'Heuristic')}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <span className={`w-2 h-2 rounded-full ${config?.hasSupabase ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            <span>Database: <strong>{config?.hasSupabase ? 'Supabase Connected' : 'Local Memory'}</strong></span>
          </div>
        </div>

        {/* Live Meta Cloud API Diagnostics & Token Management */}
        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" />
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Meta Cloud API & Gemini Diagnostics
              </h4>
            </div>
            <button
              onClick={loadDiagnostics}
              disabled={loadingDiagnostics}
              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loadingDiagnostics ? 'animate-spin' : ''}`} />
              <span>Check API Health</span>
            </button>
          </div>

          {diagnostics?.meta?.status === 'token_expired' || diagnostics?.meta?.httpStatus === 401 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-amber-900">Meta WhatsApp Access Token Expired (Code 190)</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-200 text-amber-950">
                      ACTION REQUIRED
                    </span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Customer messages reach the webhook and are analyzed by Gemini, but Meta Cloud API requires a renewed System User Token to send outbound WhatsApp replies.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-amber-200/60">
                <label className="text-xs font-semibold text-amber-900 block mb-1.5">
                  Update Meta WhatsApp Access Token (Activates immediately)
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="password"
                    value={newTokenInput}
                    onChange={(e) => setNewTokenInput(e.target.value)}
                    placeholder="Paste permanent System User Token (EAA...)"
                    className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 text-slate-800"
                  />
                  <button
                    onClick={handleUpdateToken}
                    disabled={updatingToken || !newTokenInput.trim()}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-xs shrink-0"
                  >
                    {updatingToken ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    <span>Verify & Activate</span>
                  </button>
                </div>

                {tokenUpdateResult && (
                  <div className={`mt-2 p-2 rounded-lg text-xs font-medium ${tokenUpdateResult.success ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-rose-100 text-rose-800 border border-rose-200'}`}>
                    {tokenUpdateResult.success ? tokenUpdateResult.message : tokenUpdateResult.error}
                  </div>
                )}

                <div className="mt-2 text-[11px] text-amber-800/80 leading-relaxed">
                  💡 <strong>To generate a permanent token:</strong> Meta Business Manager → Settings → System Users → Select User → Generate Token → Select App & grant <code>whatsapp_business_messaging</code>.
                </div>
              </div>
            </div>
          ) : diagnostics?.meta?.status === 'connected' ? (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <span className="font-bold text-emerald-950 block">Meta Cloud API Live & Connected</span>
                  <span className="text-emerald-800 text-[11px]">
                    Verified: {diagnostics.meta.details?.verifiedName || 'JobMatch NG'} • Quality: {diagnostics.meta.details?.qualityRating || 'GREEN'}
                  </span>
                </div>
              </div>
              <span className="text-[11px] text-emerald-700 font-semibold px-2.5 py-1 bg-emerald-100 rounded-lg self-start sm:self-auto">
                Outbound Delivery Active
              </span>
            </div>
          ) : (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-600 flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin" />
              <span>Checking live Meta Cloud API and Gemini health...</span>
            </div>
          )}
        </div>
      </div>

      {/* Live Meta Outbound Test Dispatcher */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base font-bold text-slate-900">Live Meta Cloud API Outbound Message Test</h3>
          </div>
          <span className="text-xs text-slate-400">Directly dispatches to a Nigerian WhatsApp number</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Recipient Phone Number (e.g. +1 555-491-7604 or Nigerian line)</label>
            <input
              type="text"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="e.g. 15554917604 or 2348012345678"
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Custom Test Message (Optional)</label>
            <input
              type="text"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Leave blank for standard JobMatch NG diagnostic message"
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
            id="useTemplateToggle"
            checked={useTemplate}
            onChange={(e) => setUseTemplate(e.target.checked)}
            className="w-3.5 h-3.5 accent-emerald-600 rounded"
          />
          <label htmlFor="useTemplateToggle" className="text-xs font-semibold text-slate-700 cursor-pointer">
            Send as Meta-approved Job Alert Template Message (uses META_WHATSAPP_JOB_ALERT_TEMPLATE)
          </label>
        </div>

        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] text-slate-500">
            Sends an authenticated outbound WhatsApp message using your configured Meta Phone Number ID and Token.
          </p>
          <button
            onClick={handleTestSend}
            disabled={sendingTest || !testPhone.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50 shadow-xs"
          >
            <Send className={`w-3.5 h-3.5 ${sendingTest ? 'animate-pulse' : ''}`} />
            <span>{sendingTest ? 'Dispatching...' : 'Send Live Test WhatsApp'}</span>
          </button>
        </div>

        {testResult && (
          <div
            className={`p-4 rounded-2xl text-xs space-y-1.5 border ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                : 'bg-amber-50 text-amber-900 border-amber-200'
            }`}
          >
            <div className="font-bold flex items-center gap-1.5">
              {testResult.success ? (
                <>
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>Meta Cloud API Dispatched Successfully!</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span>Outbound Message Dispatch Notice</span>
                </>
              )}
            </div>
            <p className="text-[11px] opacity-90 font-mono">
              Target: +{testResult.targetRecipient || testPhone} | Status: {testResult.result?.status || (testResult.success ? 'sent' : 'failed')}
            </p>
            {testResult.result?.wamid && (
              <p className="text-[10px] opacity-75 font-mono">
                Message ID: {testResult.result.wamid}
              </p>
            )}
            {testResult.result?.error && (
              <p className="text-[11px] text-red-700 font-sans mt-1">
                Notice: {testResult.result.error}
              </p>
            )}
            {testResult.error && (
              <p className="text-[11px] text-red-700 font-sans mt-1">
                Notice: {testResult.error}
              </p>
            )}
            {(testResult?.result?.errorCode === 131030 ||
              testResult?.result?.isAllowedListError ||
              (testResult?.result?.error || '').includes('131030') ||
              (testResult?.error || '').includes('131030')) && (
              <div className="mt-3 p-3 bg-white/90 border border-amber-300 rounded-xl text-xs text-amber-900 space-y-1.5 shadow-xs">
                <div className="font-bold flex items-center gap-1 text-amber-950">
                  <span>💡 Meta Cloud API: Recipient Not on Allowed Test List (Code 131030)</span>
                </div>
                <p className="text-[11px] leading-relaxed">
                  Your Meta WhatsApp App is in <strong>Development Mode</strong>. In Dev Mode, Meta only permits messages to phone numbers explicitly added to your Developer Dashboard test list.
                </p>
                <div className="text-[11px] bg-amber-50 p-2 rounded-lg border border-amber-200">
                  <strong>Quick Fix:</strong>
                  <ol className="list-decimal list-inside mt-1 space-y-0.5">
                    <li>Go to Meta for Developers → Your App → <strong>WhatsApp → API Setup</strong></li>
                    <li>Under <strong>Step 5 (Send and receive messages)</strong>, click <strong>Manage phone number list</strong></li>
                    <li>Add <code>+{testResult.targetRecipient || testPhone}</code> and verify with the SMS OTP</li>
                    <li>Or toggle your Meta App from <em>Development</em> to <em>Live</em> mode</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Total Contacts</span>
          <p className="text-2xl font-black text-slate-900">{metrics.totalContacts}</p>
          <span className="text-[10px] text-slate-400">Unique WhatsApp users</span>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Active Sessions</span>
          <p className="text-2xl font-black text-emerald-600">{metrics.activeConversations}</p>
          <span className="text-[10px] text-slate-400">Ongoing dialogues</span>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Jobs Created</span>
          <p className="text-2xl font-black text-slate-900">{metrics.jobsCreated}</p>
          <span className="text-[10px] text-slate-400">Real Supabase jobs</span>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Received (Inbound)</span>
          <p className="text-2xl font-black text-slate-900">{metrics.messagesReceived}</p>
          <span className="text-[10px] text-slate-400">Customer queries</span>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Sent (Outbound)</span>
          <p className="text-2xl font-black text-slate-900">{metrics.messagesSent}</p>
          <span className="text-[10px] text-slate-400">AI replies & alerts</span>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[11px] font-semibold text-slate-500 block mb-1">Failed Deliveries</span>
          <p className="text-2xl font-black text-slate-400">{metrics.failedMessages}</p>
          <span className="text-[10px] text-slate-400">Retry queue: 0</span>
        </div>
      </div>

      {/* Simulator & Test Console */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            <h3 className="text-base font-bold text-slate-900">WhatsApp Assistant Live Simulator</h3>
          </div>
          <span className="text-xs text-slate-400">Test conversational onboarding in real-time</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Customer Phone</label>
            <input
              type="text"
              value={simPhone}
              onChange={(e) => setSimPhone(e.target.value)}
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Customer Name</label>
            <input
              type="text"
              value={simName}
              onChange={(e) => setSimName(e.target.value)}
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Quick Sample Prompt</label>
            <select
              onChange={(e) => setSimText(e.target.value)}
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="My bathroom pipe is leaking and water is spilling everywhere in Ikeja">
                Plumber: Bathroom pipe leaking (Ikeja)
              </option>
              <option value="My generator is smoking and not starting, I live in Surulere">
                Gen Tech: Generator smoking (Surulere)
              </option>
              <option value="Need an electrician to install inverter and solar panels in Lekki">
                Electrician: Inverter & Solar (Lekki)
              </option>
              <option value="My split AC is blowing warm air in Abuja">
                AC Tech: Warm air blowing (Abuja)
              </option>
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={simText}
            onChange={(e) => setSimText(e.target.value)}
            placeholder="Type customer message (e.g. 'I need a carpenter to fix my broken kitchen door in Yaba')..."
            className="flex-1 text-xs px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSimulate();
            }}
          />
          <button
            onClick={() => handleSimulate()}
            disabled={simulating || !simText.trim()}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" />
            <span>{simulating ? 'Processing...' : 'Send Message'}</span>
          </button>
        </div>

        {/* Quick Action Buttons for Multi-Step State Testing */}
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
          <span className="text-slate-400 font-medium">Quick state triggers:</span>
          <button
            onClick={() => handleSimulate({ type: 'interactive', buttonId: 'CONFIRM_JOB', text: 'CONFIRM JOB' })}
            disabled={simulating}
            className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-lg font-bold"
          >
            1️⃣ Confirm Job (Post to Marketplace)
          </button>
          <button
            onClick={() => handleSimulate({ type: 'text', text: 'VIEW QUOTES' })}
            disabled={simulating}
            className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 rounded-lg font-bold"
          >
            📋 View Received Quotes
          </button>
          <button
            onClick={() => handleSimulate({ type: 'text', text: 'ACCEPT 1' })}
            disabled={simulating}
            className="px-3 py-1 bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200 rounded-lg font-bold"
          >
            🤝 Accept Quote #1
          </button>
          <button
            onClick={() => handleSimulate({ type: 'text', text: 'CANCEL' })}
            disabled={simulating}
            className="px-3 py-1 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-lg font-bold"
          >
            ❌ Cancel Draft
          </button>
        </div>

        {/* Simulation Output Display */}
        {simResult && (
          <div className="p-4 bg-slate-900 text-slate-100 rounded-2xl text-xs space-y-2 border border-slate-800">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="text-emerald-400 font-bold flex items-center gap-1.5">
                <Bot className="w-3.5 h-3.5" />
                WhatsApp Assistant Instant Reply
              </span>
              <span className="text-[11px] text-slate-400">
                New State: <strong className="text-white">{simResult.conversationState}</strong>
              </span>
            </div>
            <p className="whitespace-pre-line text-slate-200 font-sans leading-relaxed">
              {simResult.replySent}
            </p>
            {simResult.jobCreated && (
              <div className="mt-2 p-2.5 bg-emerald-950/60 border border-emerald-800/60 rounded-xl text-emerald-300">
                ✅ <strong>Real Job Created in Supabase:</strong> #{simResult.jobCreated.id} (Category: {simResult.jobCreated.category_name})
                <br />
                Artisans Matched & Notified: <strong>{simResult.matchedProsCount || 0}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conversations Table */}
      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
        <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">Recent WhatsApp Conversations</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Inspect user dialogues, draft scopes, and associated JobMatch NG jobs
            </p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full">
            {conversations.length} total threads
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs">Loading WhatsApp conversations...</div>
        ) : conversations.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            No WhatsApp conversations recorded yet. Use the simulator above to initiate one!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-5">Contact</th>
                  <th className="py-3 px-5">State</th>
                  <th className="py-3 px-5">Problem / Associated Job</th>
                  <th className="py-3 px-5">Messages</th>
                  <th className="py-3 px-5">Last Activity</th>
                  <th className="py-3 px-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {conversations.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/75 transition-colors">
                    <td className="py-4 px-5">
                      <div className="font-bold text-slate-900">{c.profileName}</div>
                      <div className="font-mono text-slate-400 text-[11px]">+{c.phoneNumber}</div>
                    </td>
                    <td className="py-4 px-5">{getStateBadge(c.state)}</td>
                    <td className="py-4 px-5 max-w-xs">
                      <div className="truncate font-semibold text-slate-800">
                        {c.jobTitle}
                      </div>
                      {c.currentJobId && (
                        <span className="inline-block mt-0.5 font-mono text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                          Job #{c.currentJobId.slice(0, 8)}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-5 font-mono text-slate-500">
                      {c.messagesCount} msgs
                    </td>
                    <td className="py-4 px-5 text-slate-400 text-[11px]">
                      {new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-4 px-5 text-right">
                      <button
                        onClick={() => openConversationMessages(c)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-all text-xs"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Message Inspection Drawer / Modal */}
      {selectedConv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-100 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm sm:text-base">{selectedConv.profileName}</h3>
                  {getStateBadge(selectedConv.state)}
                </div>
                <p className="text-xs text-slate-400 font-mono mt-0.5">+{selectedConv.phoneNumber}</p>
              </div>
              <button
                onClick={() => setSelectedConv(null)}
                className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-all text-xs font-bold"
              >
                ✕
              </button>
            </div>

            {/* Conversation Thread */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3.5 bg-slate-50">
              {conversationError ? (
                <div className="text-center py-10 px-4 text-xs text-rose-600 font-semibold bg-rose-50 border border-rose-200 rounded-2xl my-4">
                  <AlertCircle className="w-5 h-5 mx-auto mb-1.5 text-rose-500" />
                  <p>{conversationError}</p>
                </div>
              ) : loadingMessages ? (
                <div className="text-center py-10 text-xs text-slate-400">Loading message history...</div>
              ) : convMessages.length === 0 ? (
                <div className="text-center py-10 text-xs text-slate-400">No messages found in this thread.</div>
              ) : (
                convMessages.map((m) => {
                  const isInbound = m.direction === 'inbound';
                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col ${isInbound ? 'items-start' : 'items-end'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl p-3.5 text-xs shadow-xs ${
                          isInbound
                            ? 'bg-white text-slate-800 border border-slate-200'
                            : 'bg-emerald-600 text-white'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 text-[10px] opacity-75 mb-1 font-semibold">
                          <span>{isInbound ? selectedConv.profileName : 'JobMatch AI'}</span>
                          <span>•</span>
                          <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="whitespace-pre-line leading-relaxed font-sans">{m.content}</p>
                        {m.mediaUrl && (
                          <div className="mt-2">
                            <img
                              src={m.mediaUrl}
                              alt="Media upload"
                              className="rounded-lg max-h-48 object-cover border border-black/10"
                            />
                          </div>
                        )}
                      </div>
                      <span className="text-[9px] text-slate-400 mt-0.5 px-1 font-mono">
                        {m.processingStatus}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-400 font-mono text-[11px]">
                Conversation ID: {selectedConv.id}
              </span>
              <button
                onClick={() => setSelectedConv(null)}
                className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all"
              >
                Close Thread
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
